import { Box, Menu, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { FamilyMember } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { NameFormat, formatName } from 'src/utils/nameFormatting';
import classes from './FamilyIndividualToggle.module.css';

type FamilyIndividualView = 'family' | 'individual';

type Props = {
  value: FamilyIndividualView;
  onChange: (value: FamilyIndividualView) => void;
  familyLabel?: string;
  individualLabel?: string;
  familyTooltip?: string;
  members?: FamilyMember[];
  onMemberSelect?: (memberId: string) => void;
};

export function FamilyIndividualToggle({
  value,
  onChange,
  familyLabel = 'Family',
  individualLabel = 'Individual',
  familyTooltip,
  members,
  onMemberSelect,
}: Props) {
  return (
    <ProfileToggle
      value={value}
      onChange={onChange}
      familyLabel={familyLabel}
      individualLabel={individualLabel}
      familyTooltip={familyTooltip}
      members={members}
      onMemberSelect={onMemberSelect}
    />
  );
}

const profileSegmentBoxStyle = {
  display: 'flex',
  borderRadius: 20,
  padding: 3,
  gap: 0,
} as const;

// The pill background is a brand.0 → brand.6 gradient with the brand.6 end always
// sitting behind the currently chosen segment, so the stronger colour anchors the
// selection regardless of which side is active. brand.0 holds across most of the
// pill so the whitish end dominates and the orange stays concentrated behind the
// chosen option.
function profileGradient(value: FamilyIndividualView): string {
  const direction = value === 'family' ? 'to left' : 'to right';
  return `linear-gradient(${direction}, var(--mantine-color-brand-0) 45%, var(--mantine-color-brand-6) 100%)`;
}

const profileSegmentStyle = {
  paddingLeft: 55,
  paddingRight: 55,
  paddingTop: 12,
  paddingBottom: 12,
  borderRadius: 16,
  whiteSpace: 'nowrap',
} as const;

const profileDropdownSegmentStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  paddingLeft: 55,
  paddingRight: 36,
  paddingTop: 12,
  paddingBottom: 12,
  borderRadius: 16,
  whiteSpace: 'nowrap',
} as const;

type ProfileToggleProps = {
  value: FamilyIndividualView;
  onChange: (value: FamilyIndividualView) => void;
  familyLabel: string;
  individualLabel: string;
  familyTooltip?: string;
  members?: FamilyMember[];
  onMemberSelect?: (memberId: string) => void;
};

// A single stable structure for the profile toggle: the family/individual
// buttons stay mounted regardless of whether the member dropdown is available
// yet. Swapping between separate components (e.g. a SegmentedControl and a
// custom menu) while family data loads would replace the DOM node mid-click and
// drop the first press.
function ProfileToggle({
  value,
  onChange,
  familyLabel,
  familyTooltip,
  individualLabel,
  members,
  onMemberSelect,
}: ProfileToggleProps) {
  const showMemberDropdown = !!(members && members.length > 0 && onMemberSelect);

  return (
    <Box style={{ ...profileSegmentBoxStyle, background: profileGradient(value) }}>
      <UnstyledButton
        onClick={() => onChange('family')}
        className={value === 'family' ? classes.profileSegmentActive : classes.profileSegment}
        style={profileSegmentStyle}
      >
        {familyTooltip ? (
          <Tooltip label={familyTooltip} withArrow>
            <span>{familyLabel}</span>
          </Tooltip>
        ) : (
          familyLabel
        )}
      </UnstyledButton>

      {showMemberDropdown ? (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <UnstyledButton
              className={
                value === 'individual' ? classes.profileSegmentActive : classes.profileSegment
              }
              style={profileDropdownSegmentStyle}
            >
              {individualLabel}
              <IconChevronDown size={20} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            {members.map(member => (
              <Menu.Item key={member.clientId} onClick={() => onMemberSelect(member.clientId)}>
                {formatName(member.client.firstName, member.client.lastName, NameFormat.NATURAL)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      ) : (
        <UnstyledButton
          onClick={() => onChange('individual')}
          className={value === 'individual' ? classes.profileSegmentActive : classes.profileSegment}
          style={profileSegmentStyle}
        >
          {individualLabel}
        </UnstyledButton>
      )}
    </Box>
  );
}
